import type { ReactNode } from 'react';
import { Icon } from '@iconify/react';
import { IconDeviceDesktop, IconDeviceMobile, IconDeviceTablet } from '@tabler/icons-react';
import { ensureLogosIconifyCollection } from '../lib/iconify-logos';
import { parseSessionClientDisplay } from '../utils/sessionClientDisplay';
import { SESSION_BROWSER_ICONIFY, SESSION_OS_ICONIFY } from '../utils/sessionClientIconify';
import styles from './SessionClientEnvironmentIcons.module.css';

ensureLogosIconifyCollection();

const ICON_SIZE = 20;
const DEVICE_NEUTRAL = 'var(--wp-text-secondary, var(--wp-feldspar-500))';

type SessionClientEnvironmentIconsProps = {
  userAgent: string | null | undefined;
  deviceType?: string | null;
  deviceLabel?: string | null;
};

function BrandIconChip({ label, icon }: { label: string; icon: string }) {
  return (
    <span className={styles.iconChip} title={label} aria-label={label}>
      <Icon icon={icon} width={ICON_SIZE} height={ICON_SIZE} className={styles.brandMark} aria-hidden />
      <span className={styles.srOnly}>{label}</span>
    </span>
  );
}

function DeviceIconChip({
  label,
  kind,
}: {
  label: string;
  kind: 'mobile' | 'tablet' | 'desktop' | 'unknown';
}) {
  const DeviceIcon =
    kind === 'mobile' ? IconDeviceMobile : kind === 'tablet' ? IconDeviceTablet : IconDeviceDesktop;
  return (
    <span className={styles.iconChip} title={label} aria-label={label}>
      <DeviceIcon size={ICON_SIZE} stroke={1.5} color={DEVICE_NEUTRAL} aria-hidden />
      <span className={styles.srOnly}>{label}</span>
    </span>
  );
}

export function SessionClientEnvironmentIcons({
  userAgent,
  deviceType,
  deviceLabel,
}: SessionClientEnvironmentIconsProps) {
  const { browser, browserLabel, os, osLabel } = parseSessionClientDisplay(userAgent);
  const deviceKey =
    deviceType === 'mobile' || deviceType === 'tablet' || deviceType === 'desktop'
      ? deviceType
      : deviceLabel
        ? 'unknown'
        : null;

  const parts: { key: string; node: ReactNode }[] = [];
  if (deviceKey) {
    parts.push({
      key: 'device',
      node: (
        <DeviceIconChip
          kind={deviceKey}
          label={deviceLabel?.trim() || 'Appareil'}
        />
      ),
    });
  }
  if (browser && browserLabel) {
    parts.push({
      key: 'browser',
      node: <BrandIconChip label={browserLabel} icon={SESSION_BROWSER_ICONIFY[browser]} />,
    });
  }
  if (os && osLabel) {
    parts.push({
      key: 'os',
      node: <BrandIconChip label={osLabel} icon={SESSION_OS_ICONIFY[os]} />,
    });
  }

  if (parts.length === 0) return null;

  return (
    <div className={styles.row} role="group" aria-label="Environnement client">
      {parts.map((part, index) => (
        <span key={part.key} className={styles.item}>
          {index > 0 ? (
            <span className={styles.divider} aria-hidden>
              +
            </span>
          ) : null}
          {part.node}
        </span>
      ))}
    </div>
  );
}
